import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { useCompanyFinance, exportFinance, updateStatement, deleteStatement } from '@/hooks/useFinance';
import { useFinanceMeta } from '@/hooks/useFinance';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import DateInput from '@/components/DateInput';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ArrowLeft, Plus, Download, Edit3, Trash2, Check, X, Loader2 } from 'lucide-react';
import { log } from '@/lib/log';

const formatINR = (n) => '₹' + Math.round(Number(n) || 0).toLocaleString('en-IN');

const Card = ({ title, amount, focus, onClick, accentClass, focused }) => (
  <button
    data-testid={`company-card-${title.toLowerCase()}`}
    onClick={onClick}
    className={`text-left rounded-2xl border-2 transition-all p-5 ${focused ? 'border-blue-600 ring-2 ring-blue-200 bg-white' : `bg-white hover:shadow-md ${accentClass}`}`}
  >
    <div className="text-sm font-semibold text-gray-600  tracking-wider">{title}</div>
    <div className="text-3xl font-bold text-gray-900 mt-2">{formatINR(amount)}</div>
  </button>
);

const EditableRow = ({ stmt, meta, fields, onSaved, onDeleted }) => {
  const [editing, setEditing] = useState(false);
  const [amount, setAmount] = useState(String(stmt.amount));
  const [subCategory, setSubCategory] = useState(stmt.sub_category);
  const [bank, setBank] = useState(stmt.bank || '');
  const [remark, setRemark] = useState(stmt.remark || '');
  const [date, setDate] = useState(stmt.date);
  const [saving, setSaving] = useState(false);

  const subs = meta?.sub_categories?.[stmt.category] || [];

  const save = async () => {
    const cleaned = String(amount).replace(/[^0-9.]/g, '');
    const numeric = Number(cleaned);
    if (!cleaned || !Number.isFinite(numeric) || numeric <= 0) {
      toast.error('Enter a valid amount, e.g. 150000');
      return;
    }
    setSaving(true);
    try {
      const updated = await updateStatement(stmt.id, {
        amount: numeric,
        sub_category: subCategory,
        bank: stmt.category === 'Income' ? bank : null,
        remark: stmt.category === 'Expense' ? remark : null,
        date,
      });
      toast.success(`Updated to ₹${numeric.toLocaleString('en-IN')}`);
      onSaved(updated);
      setEditing(false);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Update failed — please try again');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm('Delete this statement?')) return;
    try {
      await deleteStatement(stmt.id);
      toast.success('Deleted');
      onDeleted(stmt.id);
    } catch {
      toast.error('Delete failed');
    }
  };

  if (!editing) {
    return (
      <div data-testid={`stmt-row-${stmt.id}`} className={`grid gap-4 px-2 py-3 items-center text-sm`} style={{gridTemplateColumns: fields.gridCols}}>
        <div>{stmt.sub_category}</div>
        <div className="text-gray-600">{stmt.date ? format(new Date(stmt.date), 'dd/MM/yy') : '—'}</div>
        <div className="font-medium">{formatINR(stmt.amount)}</div>
        {fields.show.includes('bank') && <div className="text-gray-700">{stmt.bank || '—'}</div>}
        {fields.show.includes('remark') && <div className="text-gray-600 italic truncate">{stmt.remark || '—'}</div>}
        <div className="flex items-center justify-end gap-1">
          <Button data-testid={`edit-stmt-${stmt.id}`} size="icon" variant="ghost" onClick={() => setEditing(true)} className="h-7 w-7"><Edit3 className="w-3.5 h-3.5" /></Button>
          <Button data-testid={`delete-stmt-${stmt.id}`} size="icon" variant="ghost" onClick={remove} className="h-7 w-7 text-red-600"><Trash2 className="w-3.5 h-3.5" /></Button>
        </div>
      </div>
    );
  }
  return (
    <div className={`grid gap-2 px-2 py-2 items-center bg-blue-50/50 rounded-lg`} style={{gridTemplateColumns: fields.gridCols}}>
      <Select value={subCategory} onValueChange={setSubCategory}>
        <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
        <SelectContent>{subs.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
      </Select>
      <DateInput testid={`date-stmt-${stmt.id}`} value={date} onChange={setDate} className="h-9" />
      <Input data-testid={`amount-stmt-${stmt.id}`} type="text" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value.replace(/[^0-9.,]/g, ''))} className="h-9" />
      {fields.show.includes('bank') && (
        <Select value={bank} onValueChange={setBank}>
          <SelectTrigger className="h-9"><SelectValue placeholder="Bank" /></SelectTrigger>
          <SelectContent>{(meta?.banks || []).map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
        </Select>
      )}
      {fields.show.includes('remark') && (
        <Input value={remark} onChange={(e) => setRemark(e.target.value)} className="h-9" placeholder="Remark" />
      )}
      <div className="flex items-center justify-end gap-1">
        <Button data-testid={`save-stmt-${stmt.id}`} size="icon" variant="ghost" onClick={save} disabled={saving} className="h-7 w-7 text-emerald-600">
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
        </Button>
        <Button data-testid={`cancel-stmt-${stmt.id}`} size="icon" variant="ghost" onClick={() => setEditing(false)} className="h-7 w-7"><X className="w-3.5 h-3.5" /></Button>
      </div>
    </div>
  );
};

const StatementsTable = ({ statements, category, meta, refresh }) => {
  const incomeCols = '1.2fr 0.7fr 1fr 0.8fr 0.6fr';
  const expenseCols = '1.2fr 0.7fr 1fr 1.5fr 0.6fr';
  const pendingCols = '1.2fr 0.7fr 1fr 0.6fr';
  const fields =
    category === 'Income'  ? { gridCols: incomeCols,  show: ['bank'] } :
    category === 'Expense' ? { gridCols: expenseCols, show: ['remark'] } :
                              { gridCols: pendingCols, show: [] };

  const headers =
    category === 'Income'  ? ['Category', 'Date', 'Amount', 'Bank', ''] :
    category === 'Expense' ? ['Category', 'Date', 'Amount', 'Remark', ''] :
                              ['Category', 'Date', 'Amount', ''];

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-4">
      <div className="grid gap-4 px-2 pb-2 text-xs font-semibold tracking-wide text-gray-500  border-b" style={{gridTemplateColumns: fields.gridCols}}>
        {headers.map((h) => <div key={h}>{h}</div>)}
      </div>
      {statements.length === 0 ? (
        <div className="p-8 text-center text-gray-500 text-sm">No {category.toLowerCase()} statements in this period.</div>
      ) : (
        <div className="divide-y divide-gray-100 mt-1">
          {statements.map((s) => (
            <EditableRow key={s.id} stmt={s} meta={meta} fields={fields}
              onSaved={() => refresh()}
              onDeleted={() => refresh()}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const CompanyFinanceView = () => {
  const { id: clientId } = useParams();
  const navigate = useNavigate();
  const meta = useFinanceMeta();
  const [period, setPeriod] = useState('monthly');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [focus, setFocus] = useState('income'); // income | expense | pending | profit
  const { data, loading, refresh } = useCompanyFinance(clientId, period, start, end);

  const handleExport = async (fmt) => {
    const scopeMap = { income: 'company-income', expense: 'company-expense', pending: 'company-pending' };
    const scope = scopeMap[focus] || 'company-income';
    const params = { client_id: clientId, period };
    if (period === 'custom') { if (start) params.start = start; if (end) params.end = end; }
    try { await exportFinance(fmt, scope, params); }
    catch (err) { log.error('[CompanyFinanceView] export failed:', err); }
  };

  if (loading || !data) return <div className="p-8 text-gray-500">Loading…</div>;

  const meta_co = data.company || {};
  const metaLine = [meta_co.place, meta_co.date_of_onboarding, meta_co.capacity, meta_co.status]
    .filter(Boolean).join('  |  ');
  const categoriesForFocus = {
    expense: data.expense_categories,
    income: data.income_categories,
    pending: data.pending_categories,
  }[focus] || [];
  const statementsForFocus = {
    expense: data.expense_statements,
    income: data.income_statements,
    pending: data.pending_statements,
  }[focus] || [];

  return (
    <div className="p-8" data-testid="company-finance-view">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div className="flex items-center gap-3">
          <button data-testid="back-btn" onClick={() => navigate('/finance')} className="p-2 rounded-full hover:bg-gray-100">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-3xl font-semibold text-gray-900 tracking-tight">{meta_co.company_name || 'Company'}</h1>
            <div className="text-sm text-gray-500 mt-1">
              {metaLine}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="add-statement-btn" onClick={() => navigate(`/finance/add?clientId=${clientId}`)} className="rounded-xl bg-blue-600 hover:bg-blue-700">
            <Plus className="w-4 h-4 mr-2" /> Add Statement
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button data-testid="export-btn" variant="outline" className="rounded-xl">
                <Download className="w-4 h-4 mr-2" /> Export
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem onClick={() => handleExport('pdf')}>PDF</DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport('docx')}>DOCX</DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport('excel')}>Excel</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Period filters */}
      <div className="flex items-center gap-2 mb-6 flex-wrap">
        {['monthly','quarterly','yearly','custom','all'].map((p) => (
          <button key={p}
            onClick={() => setPeriod(p)}
            data-testid={`period-${p}`}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${period===p ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
          >{p === 'all' ? 'All Time' : p[0].toUpperCase()+p.slice(1)}</button>
        ))}
        {period === 'custom' && (
          <>
            <DateInput testid="custom-start" value={start} onChange={setStart} className="h-9 w-40" />
            <span className="text-gray-400">→</span>
            <DateInput testid="custom-end" value={end} onChange={setEnd} className="h-9 w-40" />
          </>
        )}
      </div>

      {/* 4 cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <Card title="Income"  amount={data.cards.income}  focus="income"  focused={focus==='income'}  onClick={() => setFocus('income')}  accentClass="border-emerald-200" />
        <Card title="Expense" amount={data.cards.expense} focus="expense" focused={focus==='expense'} onClick={() => setFocus('expense')} accentClass="border-red-200" />
        <Card title="Pending" amount={data.cards.pending} focus="pending" focused={focus==='pending'} onClick={() => setFocus('pending')} accentClass="border-amber-200" />
        <Card title="Profit"  amount={data.cards.profit}  focus="profit"  focused={focus==='profit'}  onClick={() => setFocus('profit')}  accentClass="border-blue-200" />
      </div>

      {focus !== 'profit' && (
        <>
          {/* Category breakdown cards */}
          <div className="mb-4 flex items-center gap-3">
            <h2 className="text-lg font-semibold text-gray-900 capitalize">{focus} Categories</h2>
            <div className="flex-1 h-px bg-gray-200" />
          </div>
          {categoriesForFocus.length === 0 ? (
            <div className="text-sm text-gray-500 italic mb-8">No {focus} categories yet.</div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
              {categoriesForFocus.map((c) => (
                <div key={c.sub_category} data-testid={`sub-card-${c.sub_category.replace(/[^a-z0-9]+/gi,'-').toLowerCase()}`} className="bg-white rounded-2xl border border-blue-200 p-4">
                  <div className="text-sm text-gray-600">{c.sub_category}</div>
                  <div className="text-xl font-bold text-gray-900 mt-1">{formatINR(c.amount)}</div>
                </div>
              ))}
            </div>
          )}

          {/* Statement table */}
          <div className="mb-3 flex items-center gap-3">
            <h2 className="text-lg font-semibold text-gray-900 capitalize">{focus} Statement</h2>
            <div className="flex-1 h-px bg-gray-200" />
          </div>
          <StatementsTable statements={statementsForFocus} category={focus[0].toUpperCase()+focus.slice(1)} meta={meta} refresh={refresh} />
        </>
      )}
      {focus === 'profit' && (
        <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center">
          <div className="text-sm text-gray-500 mb-2">Profit calculation for this period</div>
          <div className="text-3xl font-bold text-gray-900">{formatINR(data.cards.income)} − {formatINR(data.cards.expense)} = {formatINR(data.cards.profit)}</div>
        </div>
      )}
    </div>
  );
};

export default CompanyFinanceView;
